import React, { useEffect, useMemo, useState } from "react";

import MarketIndexCard from "../../../../features/kquant/components/MarketIndexCard";
import { chartData } from "../../../HomePage/mockData";
import type { QuestionnaireSubmitPayload } from "../../types";
import {
  market_analysis,
  type MarketAnalysisResult,
} from "../../tools/market_analysis";

function YellowValue({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded bg-yellow-100/80 px-2 py-0.5 font-semibold text-yellow-800 border border-yellow-200/60 shadow-sm">
      {children}
    </span>
  );
}

function GrayValue({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 font-semibold text-slate-700 border border-slate-200">
      {children}
    </span>
  );
}

export function MarketAssistPanel({
  assistId,
  payload,
  isLocked,
  onLock,
  onConfirm,
}: {
  assistId: number;
  payload: QuestionnaireSubmitPayload;
  isLocked: boolean;
  onLock: (assistId: number) => void;
  onConfirm: (result: MarketAnalysisResult) => void;
}) {
  const [result, setResult] = useState<MarketAnalysisResult | null>(null);
  const [isExpanded, setIsExpanded] = useState(!isLocked); // Collapsed if locked

  useEffect(() => {
    // If already locked, skip animation and show result immediately
    if (isLocked && result) return;

    let alive = true;
    const delay = isLocked ? 0 : 450; // No delay if locked
    const t0 = window.setTimeout(() => {
      market_analysis(payload)
        .then((r) => {
          if (!alive) return;
          setResult(r);
        })
        .catch(() => {
          if (!alive) return;
          setResult({
            recommended: {
              marketTitle: "US Stocks",
              frequencyTitle: "Daily",
              durationTitle: "6 months",
            },
            user: {
              riskTitle: "—",
              returnTitle: "—",
              interpretTitle: "—",
              constraintsTitle: "None",
            },
          });
        });
    }, delay);

    return () => {
      alive = false;
      window.clearTimeout(t0);
    };
  }, [payload, isLocked, result]);

  const formRows = useMemo(() => {
    if (!result) return [];
    return [
      {
        label: "Market",
        value: result.recommended.marketTitle,
        kind: "model" as const,
      },
      {
        label: "Frequency",
        value: result.recommended.frequencyTitle,
        kind: "model" as const,
      },
      {
        label: "Duration",
        value: result.recommended.durationTitle,
        kind: "model" as const,
      },
      { label: "Risk", value: result.user.riskTitle, kind: "user" as const },
      {
        label: "Return",
        value: result.user.returnTitle,
        kind: "user" as const,
      },
      {
        label: "Interpret",
        value: result.user.interpretTitle,
        kind: "user" as const,
      },
      {
        label: "Constraints",
        value: result.user.constraintsTitle,
        kind: "user" as const,
      },
    ];
  }, [result]);

  return (
    <div className="w-full">
      {isLocked ? (
        // Locked state: Show compact summary only
        <div className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-[13px] font-semibold text-gray-800">
            AI-assisted selection
          </div>
          <div className="text-[13px] leading-relaxed text-gray-600">
            {result ? (
              <>
                Selected: {" "}
                <YellowValue>{result.recommended.marketTitle}</YellowValue> with a{" "}
                <YellowValue>{result.recommended.durationTitle}</YellowValue> test window.
              </>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500">
                <span className="h-3 w-3 animate-ping rounded-full bg-emerald-400/70" />
                Loading...
              </span>
            )}
          </div>
        </div>
      ) : (
        // Unlocked state: Show full content with animation
        <>
          <div className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-[13px] font-semibold text-gray-800">
              AI-assisted selection
            </div>
            <div className="text-[13px] leading-relaxed text-gray-600">
              {result ? (
                <>
                  I recommend starting with {" "}
                  <YellowValue>{result.recommended.marketTitle}</YellowValue> and a{" "}
                  <YellowValue>{result.recommended.durationTitle}</YellowValue> test window.
                </>
              ) : (
                <span className="inline-flex items-center gap-2 text-gray-500">
                  <span className="h-3 w-3 animate-ping rounded-full bg-emerald-400/70" />
                  Running market analysis...
                </span>
              )}
            </div>
          </div>
          {result && (
            <div className="mx-auto my-5 w-full max-w-[420px]">
              <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-gray-900/5 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-gray-50/50 to-white" />
                <MarketIndexCard
                  mockData={chartData}
                  readOnly
                  theme="light"
                  className="relative z-10"
                  initialRange="5D"
                  initialSymbols={['.INX:INDEXSP', 'NVDA:NASDAQ', 'TSLA:NASDAQ']}
                />
              </div>
            </div>
          )}
          {result && (
            <div className="mx-auto w-full max-w-[420px]">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="text-sm font-semibold text-gray-900">Proposed inputs</div>
                <div className="mt-3 grid grid-cols-1 gap-3">
                  {formRows.map((r) => (
                    <div key={r.label} className="flex items-start justify-between gap-4">
                      <div className="text-xs font-medium text-gray-500 pt-1">{r.label}</div>
                      <div className="text-right text-[13px] text-gray-800">
                        {r.kind === "model" ? (
                          <YellowValue>{r.value}</YellowValue>
                        ) : (
                          <GrayValue>{r.value}</GrayValue>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={() => {
                    if (isLocked) return;
                    onLock(assistId);
                    if (isLocked) return;
                    onLock(assistId);
                    window.setTimeout(() => result && onConfirm(result), 250);
                  }}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  Confirm and build workflow
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
